import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { WebhookService } from '@/features/webhooks/WebhookService';

import { TAppState } from '@/store/reducers/reducers';
import { IWebhook } from '@/features/webhooks/store/webhook.model';

import { TCoreAction } from '@/store/types/modals.types';
import { TAppError } from '@/store/types/base.state';
import { IWebhookState, TWebhookAction, WebhookActionTypes } from '@/features/webhooks/webhook.types';

import { closeModal } from '@/store/actions/core.actions';

export const searchWebhooks = (): ThunkAction<Promise<void>, TAppState, {}, TWebhookAction> => {
  const request = (): TWebhookAction => ({
    type: WebhookActionTypes.SEARCH_WEBHOOKS,
  });

  const fail = (error: TAppError): TWebhookAction => ({
    type: WebhookActionTypes.SEARCH_WEBHOOKS_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/webhooks/introduction.html#what-is-a-webhook',
  });

  const success = (webhooks: IWebhook[]): TWebhookAction => ({
    type: WebhookActionTypes.SEARCH_WEBHOOKS_SUCCESS,
    shouldSkipToaster: true,
    webhooks,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TWebhookAction>): Promise<void> => {
    dispatch(request());
    try {
      let webhooks = await WebhookService.searchWebhooks();

      webhooks = await Promise.all(webhooks.map((webhook) => WebhookService.searchWebhookEvents(webhook)));

      dispatch(success(webhooks));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const addWebhook = (
  webhook: IWebhook,
): ThunkAction<Promise<void>, TAppState, {}, TWebhookAction | TCoreAction> => {
  const request = (): TWebhookAction => ({
    type: WebhookActionTypes.ADD_WEBHOOK,
  });

  const fail = (error: TAppError): TWebhookAction => ({
    type: WebhookActionTypes.ADD_WEBHOOK_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/webhooks/introduction.html#what-is-a-webhook',
  });

  const success = (webhook: IWebhook): TWebhookAction => ({
    type: WebhookActionTypes.ADD_WEBHOOK_SUCCESS,
    webhook,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TWebhookAction | TCoreAction>): Promise<void> => {
    dispatch(request());
    try {
      const updatedWebhook = await WebhookService.addWebhook(webhook);

      dispatch(success(updatedWebhook));
      setTimeout(() => {
        dispatch(searchWebhooks());
      }, 1000);
      dispatch(closeModal());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const updateWebhook = (
  webhook: IWebhook,
  events: string[],
  onFinish?: () => void,
): ThunkAction<Promise<void>, IWebhookState, {}, TWebhookAction> => {
  const request = (): TWebhookAction => ({
    type: WebhookActionTypes.UPDATE_WEBHOOK,
  });

  const fail = (error: TAppError): TWebhookAction => ({
    type: WebhookActionTypes.UPDATE_WEBHOOK_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/webhooks/introduction.html#what-is-a-webhook',
  });

  const success = (webhook: IWebhook): TWebhookAction => ({
    type: WebhookActionTypes.UPDATE_WEBHOOK_SUCCESS,
    webhook,
  });

  return async (dispatch: ThunkDispatch<IWebhookState, {}, TWebhookAction>): Promise<void> => {
    dispatch(request());
    try {
      const updatedWebhook: IWebhook | null = await WebhookService.updateEvents(webhook, events);

      if (!updatedWebhook) throw new Error('No webhook returned, please contact support');

      dispatch(success(updatedWebhook));
      onFinish?.();
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const removeWebhook = (webhook: IWebhook): ThunkAction<Promise<void>, TAppState, {}, TWebhookAction> => {
  const request = (): TWebhookAction => ({
    type: WebhookActionTypes.REMOVE_WEBHOOK,
  });

  const fail = (error: TAppError): TWebhookAction => ({
    type: WebhookActionTypes.REMOVE_WEBHOOK_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/webhooks/introduction.html#what-is-a-webhook',
  });

  const success = (webhookId: string): TWebhookAction => ({
    type: WebhookActionTypes.REMOVE_WEBHOOK_SUCCESS,
    webhookId,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TWebhookAction>): Promise<void> => {
    dispatch(request());
    try {
      const webhookId = await WebhookService.removeWebhook(webhook);

      dispatch(success(webhookId));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};
